import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { useAuth } from '../contexts/AuthContext'
import { REFERRAL_CREDITS } from '../utils/referral'
import type { ReferralRecord, ReferralStats } from '../types/referral'
interface UseReferralsReturn {
  referralCode: string | null
  scanCredits: number
  referrals: ReferralRecord[]
  stats: ReferralStats
  loading: boolean
  error: string | null
  refetch: () => void
}
function normalizeReferral(row: Record<string, unknown>): ReferralRecord {
  const rewardGiven = Boolean(row.reward_given)
  const rawStatus = typeof row.status === 'string' ? row.status : null
  const isRewarded =
    rewardGiven ||
    rawStatus === 'rewarded' ||
    rawStatus === 'completed' ||
    rawStatus === 'complete'
  const email =
    (typeof row.referred_email === 'string' && row.referred_email) ||
    (typeof row.email === 'string' && row.email) ||
    null
  const createdAt =
    (typeof row.created_at === 'string' && row.created_at) ||
    (typeof row.inserted_at === 'string' && row.inserted_at) ||
    null
  return {
    id: String(row.id ?? row.referred_user_id ?? Math.random()),
    email,
    createdAt,
    status: isRewarded ? 'rewarded' : 'pending',
    rewardGiven,
  }
}
/**
 * Reads the current user's referral code, credit balance and referral history.
 * Reward validation is owned by the backend — this hook only reads.
 */
export function useReferrals(): UseReferralsReturn {
  const { user } = useAuth()
  const [referralCode, setReferralCode] = useState<string | null>(null)
  const [scanCredits, setScanCredits] = useState(0)
  const [referrals, setReferrals] = useState<ReferralRecord[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const refetch = useCallback(() => setReloadKey((key) => key + 1), [])
  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!user?.id) {
        setLoading(false)
        return
      }
      setLoading(true)
      setError(null)
      try {
        const [profileResult, referralResult] = await Promise.all([
          supabase.from('profiles').select('*').eq('id', user.id).maybeSingle(),
          supabase.from('referrals').select('*').eq('referrer_id', user.id),
        ])
        if (cancelled) return
        if (profileResult.error) throw profileResult.error
        if (referralResult.error) throw referralResult.error
        const profile = (profileResult.data ?? {}) as Record<string, unknown>
        setReferralCode(
          typeof profile.referral_code === 'string'
            ? profile.referral_code
            : null,
        )
        setScanCredits(
          typeof profile.scan_credits === 'number' ? profile.scan_credits : 0,
        )
        const rows = (referralResult.data ?? []) as Record<string, unknown>[]
        const normalized = rows.map(normalizeReferral).sort((a, b) => {
          if (!a.createdAt || !b.createdAt) return 0
          return (
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
        })
        setReferrals(normalized)
      } catch (err) {
        if (cancelled) return
        console.error('Error loading referrals:', err)
        setError('We could not load your referral activity. Please try again.')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [user?.id, reloadKey])
  const rewarded = referrals.filter((item) => item.status === 'rewarded')
  const stats: ReferralStats = {
    totalInvited: referrals.length,
    earnedCredits: rewarded.length * REFERRAL_CREDITS,
    pendingInvites: referrals.length - rewarded.length,
  }
  return {
    referralCode,
    scanCredits,
    referrals,
    stats,
    loading,
    error,
    refetch,
  }
}
